import { useQueryClient } from '@tanstack/react-query';
import { useReducedMotion } from 'motion/react';
import { useRouter } from 'next/router';
import { type MutableRefObject, memo, type TransitionEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import {
  hydrateSelectedItemsAction,
  resetSelectedItemsAction,
  selectItemAction,
  setSelectableItemsAction,
} from '@air/redux-selected-items';

import {
  ScrollElementContextConsumer,
  ScrollElementContextProvider,
} from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { PrivateWorkspaceAgentAssetListPage } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListPage';
import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';
import { useWorkspaceSelectionBarStore } from '~/components/v2/store/features/workspace/useWorkspaceSelectionBarStore';
import { PERSISTED_SELECTIONS } from '~/constants/localStorageKeys';
import { getPrivateMediaPlayerKey } from '~/constants/react-query-keys';
import { QueryParamNames } from '~/constants/search';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { MouseSelectionProvider } from '~/providers/MouseSelectionProvider';
import { type MediaPlayerAssetsInfo } from '~/swr-hooks/mediaPlayer/useMediaPlayerData';
import { useAirStore } from '~/utils/ReduxUtils';
import { appStorage } from '~/utils/Storage';

const TAKEOVER_MOTION_MS = 240;
/** Extra wait after the slide so unmount does not clip the last frame. */
const TAKEOVER_UNMOUNT_BUFFER_MS = 40;

const resolveTakeoverId = ({
  isAgentRoute,
  queryId,
  queryIsDismissed,
  storeId,
}: {
  isAgentRoute: boolean;
  queryId: string | undefined;
  queryIsDismissed: boolean;
  storeId: string | null | undefined;
}): string | undefined => {
  if (isAgentRoute) {
    return undefined;
  }

  if (storeId) {
    return storeId;
  }

  if (queryIsDismissed) {
    return undefined;
  }

  return queryId;
};

const readQueryString = (value: string | string[] | undefined): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

type TakeoverScrollSurfaceProps = {
  artifactId: string;
  scrollElementRef: MutableRefObject<HTMLDivElement | HTMLElement | null>;
  sessionId: string;
  setScrollElement: (element: HTMLElement | null) => void;
};

const TakeoverScrollSurface = memo(
  ({ artifactId, scrollElementRef, sessionId, setScrollElement }: TakeoverScrollSurfaceProps) => {
    const handleRef = useCallback(
      (node: HTMLDivElement | null) => {
        scrollElementRef.current = node;
        setScrollElement(node);
      },
      [scrollElementRef, setScrollElement],
    );

    return (
      <div
        className="flex size-full min-h-0 min-w-0 flex-col overflow-y-auto ads:bg-base bg-gray-1"
        data-testid="WORKSPACE_AGENT_ASSET_LIST_TAKEOVER_SURFACE"
        ref={handleRef}
      >
        <PrivateWorkspaceAgentAssetListPage artifactId={artifactId} sessionId={sessionId} />
      </div>
    );
  },
);

TakeoverScrollSurface.displayName = 'TakeoverScrollSurface';

export const PrivateWorkspaceAgentAssetListTakeover = memo(() => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const store = useAirStore();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const isAgentRoute = router.pathname.startsWith('/agent/');
  const queryArtifactId = readQueryString(router.query[QueryParamNames.artifactId]);
  const querySessionId =
    readQueryString(router.query[QueryParamNames.chatId]) ?? readQueryString(router.query.sessionId);

  const storeArtifactId = useWorkspaceAgentAssetListViewStore((state) => state.activeArtifactId);
  const storeSessionId = useWorkspaceAgentAssetListViewStore((state) => state.activeSessionId);
  const dismissedArtifactIds = useWorkspaceAgentAssetListViewStore((state) => state.dismissedArtifactIds);
  const openArtifactTab = useWorkspaceAgentAssetListViewStore((state) => state.openArtifactTab);
  const closeArtifactView = useWorkspaceAgentAssetListViewStore((state) => state.closeArtifactView);
  const releaseDismissedArtifactIds = useWorkspaceAgentAssetListViewStore((state) => state.releaseDismissedArtifactIds);
  const queryIsDismissed = Boolean(queryArtifactId && Object.hasOwn(dismissedArtifactIds, queryArtifactId));

  const prevQueryArtifactIdRef = useRef<string | undefined>(queryArtifactId);

  useEffect(() => {
    if (!router.isReady || isAgentRoute) {
      return;
    }

    const prevQueryArtifactId = prevQueryArtifactIdRef.current;
    prevQueryArtifactIdRef.current = queryArtifactId;

    if (queryIsDismissed) {
      return;
    }

    releaseDismissedArtifactIds();

    const { activeArtifactId: currentStoreArtifactId, activeSessionId: currentStoreSessionId } =
      useWorkspaceAgentAssetListViewStore.getState();

    if (queryArtifactId && querySessionId) {
      if (currentStoreArtifactId !== queryArtifactId || currentStoreSessionId !== querySessionId) {
        openArtifactTab({ artifactId: queryArtifactId, sessionId: querySessionId });
      }

      return;
    }

    if (!queryArtifactId && prevQueryArtifactId && currentStoreArtifactId) {
      closeArtifactView();
    }
  }, [
    closeArtifactView,
    isAgentRoute,
    openArtifactTab,
    queryArtifactId,
    queryIsDismissed,
    querySessionId,
    releaseDismissedArtifactIds,
    router.isReady,
  ]);

  const activeArtifactId = resolveTakeoverId({
    isAgentRoute,
    queryId: queryArtifactId,
    queryIsDismissed,
    storeId: storeArtifactId,
  });
  const activeSessionId = resolveTakeoverId({
    isAgentRoute,
    queryId: querySessionId,
    queryIsDismissed,
    storeId: storeSessionId,
  });
  const isOpen = Boolean(activeArtifactId && activeSessionId);

  const lastIdsRef = useRef<{ artifactId: string; sessionId: string } | null>(null);

  if (activeArtifactId && activeSessionId) {
    lastIdsRef.current = { artifactId: activeArtifactId, sessionId: activeSessionId };
  }

  const reduceMotion = useReducedMotion() === true;
  const [mounted, setMounted] = useState(isOpen);
  const [shown, setShown] = useState(isOpen && reduceMotion);
  const takeoverRef = useRef<HTMLDivElement>(null);
  const scrollElementRef = useRef<HTMLDivElement | HTMLElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (reduceMotion) {
      setMounted(isOpen);
      setShown(isOpen);
      return;
    }

    if (isOpen) {
      setMounted(true);
      setShown(false);
      const frame = requestAnimationFrame(() => setShown(true));

      return () => cancelAnimationFrame(frame);
    }

    setShown(false);
  }, [isOpen, reduceMotion]);

  useEffect(() => {
    if (isOpen || !mounted || reduceMotion) {
      return;
    }

    const timer = window.setTimeout(() => setMounted(false), TAKEOVER_MOTION_MS + TAKEOVER_UNMOUNT_BUFFER_MS);

    return () => window.clearTimeout(timer);
  }, [isOpen, mounted, reduceMotion]);

  useIsomorphicLayoutEffect(() => {
    if (!mounted) {
      return;
    }

    useMainSideNavStore.getState().setSuppressPinned(true);

    const previousPortal = useWorkspaceSelectionBarStore.getState().portal;

    if (takeoverRef.current !== null) {
      useWorkspaceSelectionBarStore.getState().setPortal(takeoverRef.current);
    }

    const { anchor, selectableItems, selected } = store.getState().selectedItems;
    const previousPersistedSelections = appStorage.getItem(PERSISTED_SELECTIONS);
    const mediaPlayerKey = getPrivateMediaPlayerKey(workspaceId);
    const previousMediaPlayerData = workspaceId
      ? queryClient.getQueryData<MediaPlayerAssetsInfo>(mediaPlayerKey)
      : undefined;

    const restorePersistedSelections = () => {
      if (previousPersistedSelections !== null) {
        appStorage.setItem(PERSISTED_SELECTIONS, previousPersistedSelections);
      } else {
        appStorage.removeItem(PERSISTED_SELECTIONS);
      }
    };

    store.dispatch(resetSelectedItemsAction());

    return () => {
      useMainSideNavStore.getState().setSuppressPinned(false);
      if (useWorkspaceSelectionBarStore.getState().portal === takeoverRef.current) {
        useWorkspaceSelectionBarStore.getState().setPortal(previousPortal);
      }
      restorePersistedSelections();
      store.dispatch(resetSelectedItemsAction());
      restorePersistedSelections();
      store.dispatch(setSelectableItemsAction({ persist: true, selectableItems }));
      if (anchor) {
        store.dispatch(selectItemAction({ item: anchor }));
      }
      store.dispatch(hydrateSelectedItemsAction({ selectedItems: selected }));
      restorePersistedSelections();
      if (workspaceId && previousMediaPlayerData !== undefined) {
        queryClient.setQueryData(mediaPlayerKey, previousMediaPlayerData);
      }
    };
  }, [mounted, queryClient, store, workspaceId]);

  const handleTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
        return;
      }
      if (!isOpen) {
        setMounted(false);
      }
    },
    [isOpen],
  );

  if (!mounted || !lastIdsRef.current) {
    return null;
  }

  const { artifactId, sessionId } = lastIdsRef.current;

  return (
    <div
      className="absolute top-0 right-0 bottom-0 left-[calc(var(--main-side-nav-width,60px)-1px)] z-40 flex min-h-0 min-w-0 flex-col overflow-hidden ads:border-default/80 border-gray-4/80 border-l ads:bg-base bg-gray-1"
      data-testid="WORKSPACE_AGENT_ASSET_LIST_TAKEOVER"
      onTransitionEnd={handleTransitionEnd}
      ref={takeoverRef}
      style={{
        transform: shown ? 'translateX(0)' : 'translateX(100%)',
        transition: reduceMotion ? 'none' : `transform ${TAKEOVER_MOTION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
      }}
    >
      <MouseSelectionProvider selectionArea={scrollElementRef}>
        <ScrollElementContextProvider>
          <ScrollElementContextConsumer>
            {({ setScrollElement }) => (
              <TakeoverScrollSurface
                artifactId={artifactId}
                scrollElementRef={scrollElementRef}
                sessionId={sessionId}
                setScrollElement={setScrollElement}
              />
            )}
          </ScrollElementContextConsumer>
        </ScrollElementContextProvider>
      </MouseSelectionProvider>
    </div>
  );
});

PrivateWorkspaceAgentAssetListTakeover.displayName = 'PrivateWorkspaceAgentAssetListTakeover';
